/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Input2, DropDown2, ToggleButton, CheckBox, DecimalInput } from "components";
import { useInput } from "hooks";
import { useMainContext } from "contexts";
import AutocompleteFreeText from "components/AutocompleteFreeText";
import { SAVE_OFFER_PREDICTIVE_TEXT, ALL_COUPON_TYPES_PREDICTIVE_TEXT } from "config";
import useAdBlockStore from "store/AdBlockStore";

export const OfferSaveTextBuilder = ({
  className,
  value,
  existingOfferText,
  onChange = () => {},
  onStartChange = () => {},
  view = "offer"
}) => {
  let currentOffer;
  if(view==="offer"){
    currentOffer = useMainContext().currentInfo;
  } else if(view === "edit"){
    currentOffer = useAdBlockStore((state) => state.currentOffer);
  }
  
  const start = useInput();
  const end = useInput("");
  const upto = useInput("");
  const extra = useInput("");
  const amount = useInput("");
  const type = useInput("$");
  const start_price = useInput();
  const end_price = useInput("");
  const bulk_amount = useInput("");
  const perc_included = useInput("");
  const isPurchase = useInput(false);
  const isAny = useInput("");
  const purchaseType = useInput("");
  const withCoupon = useInput("");
  const [bulkCalculator, setBulkCalculator] = useState(false);
  const [calculatorType, setCalculatorType] = useState(false);
  const appliesToEach = useInput(false);
  const typeData = [
    { id: "%", title: "%" },
    { id: "$", title: "$" },
  ];
  const freeText = useInput("");

  const isInvalidFreeTextValue = (val) => {
    if (val === undefined || val === null) {
      return false;
    }
    const normalized = `${val}`.trim();
    if (!normalized) {
      return false;
    }
    // Disallow free text that is only numeric content, with optional leading '$'.
    return /^\$?\s*[\d,.]+$/.test(normalized);
  };

  const buildSaveText = ({
    startValue,
    endValue,
    amountValue,
    typeValue,
    freeTextValue,
    eachValue,
    uptoValue,
    extraValue,
    withCouponValue,
    isPurchaseValue,
    isAnyValue,
    purchaseTypeValue,
  }) => {
    const hasValue = (val) => !(val === undefined || val === null || `${val}`.trim() === "");
    const normalize = (val) => (hasValue(val) ? `${val}`.trim() : "");
    const placeholder = uptoValue ? "up to" : extraValue ? "an extra" : "";
    const formatValue = (val) => {
      if (!hasValue(val)) {
        return "";
      }
      const normalized = normalize(val);
      if (typeValue === "$") {
        return `$${normalized}`;
      }
      if (typeValue === "%") {
        return `${normalized}%`;
      }
      return normalized;
    };

    const formattedStart = formatValue(startValue);
    const formattedEnd = hasValue(endValue) ? formatValue(endValue) : "";
    const valueSegment = formattedStart
      ? formattedEnd
        ? `${formattedStart}-${formattedEnd}`
        : formattedStart
      : "";

    const segments = [];
    segments.push(["save", placeholder].filter(Boolean).join(" "));
    if (valueSegment) {
      segments.push(valueSegment);
    }
    if (eachValue) {
      segments.push("each");
    }

    const amountTokens = [];
    if (isAnyValue) {
      amountTokens.push("any");
    }
    const normalizedAmount = normalize(amountValue);
    if (purchaseTypeValue === "$") {
      if (normalizedAmount) {
        amountTokens.push(`$${normalizedAmount}`);
      } else if (isPurchaseValue) {
        amountTokens.push("$");
      }
    } else if (normalizedAmount) {
      amountTokens.push(normalizedAmount);
    }

    const amountSegment = amountTokens.join(" ");
    if (isPurchaseValue) {
      segments.push(amountSegment ? `w/purchase/${amountSegment}` : "w/purchase/");
    } else if (amountSegment) {
      segments.push(amountSegment);
    }

    const normalizedFreeText = normalize(freeTextValue);
    if (normalizedFreeText) {
      if (!isInvalidFreeTextValue(normalizedFreeText)) {
        segments.push(normalizedFreeText);
      }
    }

    const normalizedCoupon = normalize(withCouponValue);
    if (normalizedCoupon) {
      segments.push(`w/${normalizedCoupon}`);
    }

    return segments.filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
  };

  const handleUpdateValue = (overrides = {}) => {
    const params = {
      startValue: start.value,
      endValue: end.value,
      amountValue: amount.value,
      typeValue: type.value,
      freeTextValue: freeText.value,
      eachValue: appliesToEach.value,
      uptoValue: upto.value,
      extraValue: extra.value,
      withCouponValue: withCoupon.value,
      isPurchaseValue: isPurchase.value,
      isAnyValue: isAny.value,
      purchaseTypeValue: purchaseType.value,
      ...overrides,
    };
    // save 7.00 (24%) FREETEXT COUPON
    onChange(buildSaveText(params));
  };

  // Unit calculator: (start_price - end_price) * bulk_amount
  const handleUnitCalculator = (start_price = existingOfferText, end_price = 0, bulk_amount = 1, perc_included, free_text, each, upto, extra) => {
    const savingAmount = ((start_price - end_price) * bulk_amount).toFixed(2);
    const percentage = perc_included ? (((start_price - end_price) / start_price) * 100).toFixed() : null;
    const placeholder = upto ? 'up to' : extra ? 'an extra' : '';
    
    const parts = ['save', placeholder, `$${savingAmount}`].filter(Boolean);
    if (percentage) parts.push(`(${percentage}%)`);
    if (free_text) parts.push(free_text);
    if (withCoupon.value) parts.push(`w/${withCoupon.value}`);
    if (each) parts.push('each');
    
    onChange(parts.join(' ').replace(/\s+/g, ' ').trim());
  };

  // Bulk calculator: (start_price * bulk_amount) - end_price
  const handleBulkCalculator = (start_price = existingOfferText, end_price = 0, bulk_amount = 1, perc_included, free_text, each, upto, extra) => {
    const savingAmount = ((start_price * bulk_amount) - end_price).toFixed(2);
    const percentage = perc_included ? ((((start_price * bulk_amount) - end_price) / (start_price * bulk_amount)) * 100).toFixed() : null;
    const placeholder = upto ? 'up to' : extra ? 'an extra' : '';
    
    const parts = ['save', placeholder, `$${savingAmount}`].filter(Boolean);
    if (percentage) parts.push(`(${percentage}%)`);
    if (free_text) parts.push(free_text);
    if (withCoupon.value) parts.push(`w/${withCoupon.value}`);
    if (each) parts.push('each');
    
    onChange(parts.join(' ').replace(/\s+/g, ' ').trim());
  };

  const handleCalculatorToggle = (e) => {
    setBulkCalculator(e);
    if (!e) {
      handleUpdateValue();
    } else if (e) {
      if (calculatorType === 'unit')
        handleUnitCalculator(start_price.value, end_price.value, bulk_amount.value,perc_included.value, freeText.value, appliesToEach.value, upto.value, extra.value)
      else {
        handleBulkCalculator(start_price.value, end_price.value, bulk_amount.value, perc_included.value, freeText.value, appliesToEach.value, upto.value, extra.value)
      }
    }
  }

  useEffect(() => {
    // Only set default value if there's no existing offer text
    if (!currentOffer?.offer_text && !value){
      start_price.setValue(existingOfferText);
      start.setValue(existingOfferText);
      onChange(`save ${existingOfferText}`);
    }
  }, [existingOfferText])

  useEffect(() => {
    if (!value || start.value) {
      return;
    }

    const sortedCouponTypes = [...ALL_COUPON_TYPES_PREDICTIVE_TEXT]
      .filter((c) => c.id)
      .sort((a, b) => b.id.length - a.id.length);

    const isNumericToken = (token) => {
      if (token === undefined || token === null) {
        return false;
      }
      const trimmed = `${token}`.trim();
      if (!trimmed) {
        return false;
      }
      return !Number.isNaN(Number(trimmed));
    };

    let workingValue = value.trim();
    if (!workingValue.toLowerCase().startsWith('save')) {
      return;
    }

    workingValue = workingValue.slice(4).trim();

    let uptoActive = false;
    let extraActive = false;
    if (workingValue.toLowerCase().startsWith('up to')) {
      uptoActive = true;
      workingValue = workingValue.slice('up to'.length).trim();
    } else if (workingValue.toLowerCase().startsWith('an extra')) {
      extraActive = true;
      workingValue = workingValue.slice('an extra'.length).trim();
    }

    let couponText = '';
    const lowerWorkingValue = workingValue.toLowerCase();
    const couponIndex = lowerWorkingValue.lastIndexOf(' w/');
    if (couponIndex !== -1) {
      const couponSegment = workingValue.slice(couponIndex + 1);
      if (!couponSegment.toLowerCase().startsWith('w/purchase/')) {
        couponText = couponSegment.slice(2).trim();
        workingValue = workingValue.slice(0, couponIndex).trim();
      }
    }

    const tokens = workingValue.split(/\s+/).filter(Boolean);
    if (!tokens.length) {
      return;
    }

    const valueToken = tokens.shift();
    const [startToken, endToken] = (valueToken || '').split('-');
    const determineType = () => {
      if (valueToken.includes('$')) {
        return '$';
      }
      if (valueToken.includes('%')) {
        return '%';
      }
      return type.value || '%';
    };
    const selectedType = determineType();
    const stripType = (token) => (token || '').replace(/[$%]/g, '').trim();

    start.setValue(stripType(startToken));
    if (endToken) {
      end.setValue(stripType(endToken));
    } else {
      end.setValue('');
    }
    type.setValue(selectedType);

    const eachActive = tokens[0]?.toLowerCase() === 'each' ? (tokens.shift(), true) : false;

    // Helper to parse amount from token array
    const parseAmountTokens = (detailTokens) => {
      let type = '', amount = '';
      if (detailTokens[0] === '$') {
        type = '$';
        detailTokens.shift();
        amount = detailTokens.shift() || '';
      } else if (detailTokens[0]?.startsWith('$')) {
        type = '$';
        amount = detailTokens.shift().slice(1);
      } else if (isNumericToken(detailTokens[0])) {
        amount = detailTokens.shift();
      }
      return { type, amount };
    };

    // Extract purchase details from w/purchase/ token or fallback to regular tokens
    let purchaseActive = false, anyActive = false, purchaseTypeValue = '', amountValue = '';
    const purchasePrefix = 'w/purchase/';

    if (tokens[0]?.toLowerCase().startsWith(purchasePrefix)) {
      purchaseActive = true;
      const rawDetails = tokens.shift().slice(purchasePrefix.length).trim();
      let detailTokens = rawDetails ? rawDetails.split(/\s+/).filter(Boolean) : [];

      // Consume matching tokens from remaining queue
      while (tokens.length && (tokens[0]?.toLowerCase() === 'any' || tokens[0] === '$' || tokens[0]?.startsWith('$') || isNumericToken(tokens[0]))) {
        if (!detailTokens.some((t) => t.toLowerCase() === 'any') && tokens[0]?.toLowerCase() === 'any') {
          detailTokens.push(tokens.shift());
        } else if (tokens[0] === '$' || tokens[0]?.startsWith('$') || isNumericToken(tokens[0])) {
          detailTokens.push(tokens.shift());
        } else break;
      }

      if (detailTokens[0]?.toLowerCase() === 'any') {
        anyActive = true;
        detailTokens.shift();
      }
      const parsed = parseAmountTokens(detailTokens);
      purchaseTypeValue = parsed.type;
      amountValue = parsed.amount;
      // Re-queue unconsumed tokens
      for (let i = detailTokens.length - 1; i >= 0; i -= 1) tokens.unshift(detailTokens[i]);
    } else {
      if (tokens[0]?.toLowerCase() === 'any') {
        anyActive = true;
        tokens.shift();
      }
      const parsed = parseAmountTokens(tokens);
      purchaseTypeValue = parsed.type;
      amountValue = parsed.amount;
    }

    const remainingText = tokens.join(' ').trim();

    appliesToEach.setValue(eachActive);
    isPurchase.setValue(purchaseActive);
    upto.setValue(uptoActive);
    extra.setValue(extraActive);
    isAny.setValue(anyActive ? '$' : '');
    purchaseType.setValue(purchaseTypeValue);

    const min_qty = currentOffer.min_qty != null && currentOffer.min_qty !== undefined && currentOffer.min_qty !== '' && currentOffer.min_qty != 0;
    amount.setValue(min_qty ? amountValue : '');
    const parsedFreeText = min_qty ? remainingText : `${amountValue} ${remainingText}`.trim();
    freeText.setValue(isInvalidFreeTextValue(parsedFreeText) ? '' : parsedFreeText);

    if (couponText) {
      const matchedCoupon = sortedCouponTypes.find(
        (coupon) => couponText.toLowerCase() === coupon.id.toLowerCase()
      );
      withCoupon.setValue(matchedCoupon ? matchedCoupon.id : couponText);
    } else {
      withCoupon.setValue('');
    }
  }, [value]);

  return (
    <BuilderContainer className={`${className} mt-1`}>
      <Title>Offer Text Builder</Title>
      <ToggleButton 
        label="Calculator"
        value={bulkCalculator} 
        onChange={(e) => handleCalculatorToggle(e)}
        className="w-100" />
      {bulkCalculator &&
        <Container>
          <div style={{ width: 65, marginRight: 10 }}>
            <Input2
              label="Start Price"
              value={start_price.value}
              type="float"
              onChange={(e) => {
                start_price.setValue(e);
              }}
            />
          </div>
          <div style={{ width: 65, marginRight: 10 }}>
            <Input2
              label="End Price"
              value={end_price.value}
              type="float"
              onChange={(e) => {
                end_price.setValue(e);
              }}
            />
          </div>
          <div style={{ width: 65, marginRight: 10 }}>
            <Input2
              label="Amount"
              value={bulk_amount.value}
              type="float"
              onChange={(e) => {
                bulk_amount.setValue(e);
              }}
            />
          </div>

          <div className="d-flex flex-column m-auto">
            <ToggleButton
              label="Include %"
              className="w-100 mb-1"
              value={perc_included.value}
              onChange={e => perc_included.setValue(e)}
              />
            <ToggleButton
              label="Unit Calculator"
              className="w-100 mb-1"
              onChange={e => {
                setCalculatorType('unit');
                handleUnitCalculator(
                  start_price.value,
                  end_price.value,
                  bulk_amount.value,
                  perc_included.value,
                  freeText.value,
                  appliesToEach.value,
                  upto.value,
                  extra.value
                  );
                }}
                />
            <ToggleButton
              label="Bulk Calculator"
              className="w-100"
              onChange={e => {
                setCalculatorType('bulk');
                handleBulkCalculator(
                  start_price.value,
                  end_price.value,
                  bulk_amount.value,
                  perc_included.value,
                  freeText.value,
                  appliesToEach.value,
                  upto.value,
                  extra.value
                );
              }}
            />
          </div>
        </Container>}
      {!bulkCalculator && <div>
        <Container>
          <div>
            <DropDown2
              label="Type"
              data={typeData}
              width={60}
              value={type.value}
              onChange={(e) => {
                type.setValue(e);
                handleUpdateValue({ typeValue: e });
              }}
            />
          </div>
          <div className="ml-auto" style={{ width: "30%" }}>
            {type.value === "$" ? (
              <DecimalInput
                label="Value Start"
                value={start.value}
                onChange={(e) => {
                  start.setValue(e);
                  onStartChange(e);
                  handleUpdateValue({ startValue: e });
                }}
              />) : (
                <Input2
              label="Value Start"
              value={start.value}
              type="int"
              onChange={(e) => {
                start.setValue(e);
                type.value === "$" && onStartChange(e);
                handleUpdateValue({ startValue: e });
              }}
            />)
              }
            
          </div>
          <div className="mt-auto mb-1 p-2 text">-</div>
          <div style={{ width: "30%", marginRight: 10 }}>
            {type.value === "$" ? (
              <DecimalInput
                label="Value End"
                value={end.value}
                onChange={(e) => {
                  end.setValue(e);
                  handleUpdateValue({ endValue: e });
                }}
              />) : (
            <Input2
              label="Value End"
              value={end.value}
              type="int"
              onChange={(e) => {
                end.setValue(e);
                handleUpdateValue({ endValue: e });
              }}
            />)
            }
          </div>
        </Container>

        <Container className="d-flex flex-column">
          <div className="d-flex">
            <ToggleButton
              label="up to"
              className="w-100 mb-1"
              value={upto.value}
              onChange={e => {
                upto.setValue(e)
                if (e) {
                  extra.setValue(false);
                }
                if (bulkCalculator) {
                  if (calculatorType === 'unit') {
                    handleUnitCalculator(
                      start_price.value,
                      end_price.value,
                      bulk_amount.value,
                      perc_included.value,
                      freeText.value,
                      appliesToEach.value,
                      e,
                      false
                    )
                  } else {
                    handleBulkCalculator(
                      start_price.value,
                      end_price.value,
                      bulk_amount.value,
                      perc_included.value,
                      freeText.value,
                      appliesToEach.value,
                      e,
                      false
                    )
                  }
                } else {
                  handleUpdateValue({ uptoValue: e, extraValue: false });
                }
              }}
            />
            <ToggleButton
              label="an extra"
              className="w-100"
              value={extra.value}
              onChange={e => {
                extra.setValue(e);
                if (e) {
                  upto.setValue(false);
                }
                if (bulkCalculator) {
                  if (calculatorType === 'unit') {
                    handleUnitCalculator(
                      start_price.value,
                      end_price.value,
                      bulk_amount.value,
                      perc_included.value,
                      freeText.value,
                      appliesToEach.value,
                      false,
                      e,
                    )
                  } else {
                    handleBulkCalculator(
                      start_price.value,
                      end_price.value,
                      bulk_amount.value,
                      perc_included.value,
                      freeText.value,
                      appliesToEach.value,
                      false,
                      e,
                    )
                  }
                } else {
                  handleUpdateValue({ extraValue: e, uptoValue: false });
                }
              }}
            />
          </div>
          <CheckBox
            value={appliesToEach.value}
            onChange={e => {
              appliesToEach.setValue(e);
              if (bulkCalculator) {
                if (calculatorType === 'unit') {
                  handleUnitCalculator(
                    start_price.value,
                    end_price.value,
                    bulk_amount.value,
                    perc_included.value,
                    freeText.value,
                    e,
                    upto.value,
                    extra.value
                  )
                } else {
                  handleBulkCalculator(
                    start_price.value,
                    end_price.value,
                    bulk_amount.value,
                    perc_included.value,
                    freeText.value,
                    e,
                    upto.value,
                    extra.value
                  )
                }
              } else {
                handleUpdateValue({ eachValue: e });
              }
            }}
          >
            Applies To Each
          </CheckBox>
        </Container>

        <Container className="mt-2">
          <ToggleButton
            label="w/purchase/"
            className="mt-auto mb-2"
            value={isPurchase.value}
            onChange={(e) => {
              isPurchase.setValue(e);
              handleUpdateValue({ isPurchaseValue: e });
            }}
          />
          
          <ToggleButton
            label="any"
            className="mt-auto mb-2 mx-2"
            value={isAny.value === "$"}
            onChange={(e) => {
              const value = e ? "$" : "";
              isAny.setValue(value);
              handleUpdateValue({ isAnyValue: value });
            }}
          />

          <ToggleButton
            label="$"
            className="mt-auto mb-2"
            value={purchaseType.value === "$"}
            onChange={(e) => {
              const value = e ? "$" : "";
              purchaseType.setValue(value);
              amount.setValue('');
              handleUpdateValue({ purchaseTypeValue: value, amountValue: '' });
            }}
          />

          <div className="w-100 ml-4">
            {purchaseType.value === "$" ? (
              <DecimalInput
                label="Amount"
                disabled={!isPurchase.value}
                value={amount.value}
                onChange={(e) => {
                  amount.setValue(e);
                  handleUpdateValue({ amountValue: e });
                }}
              />
            ) : (
              <Input2
                label="Amount"
                type="int"
                value={amount.value}
                onChange={(e) => {
                  const sanitizedValue = e.replace(" ", "");
                  amount.setValue(sanitizedValue);
                  handleUpdateValue({ amountValue: sanitizedValue });
                }}
              />)
            }
          </div>

        </Container>
      </div>
      }

      <Container>
        <div className="ml-auto" style={{ width: "50%" }}>
          <AutocompleteFreeText
            className="w-100"
            label='Free Text'
            value={freeText.value}
            onChange={e => {
              freeText.setValue(e);
              const safeFreeText = isInvalidFreeTextValue(e) ? '' : e;
              if (bulkCalculator) {
                if (calculatorType === 'unit') {
                  handleUnitCalculator(
                    start_price.value,
                    end_price.value,
                    bulk_amount.value,
                    perc_included.value,
                    safeFreeText,
                    appliesToEach.value,
                    upto.value,
                    extra.value
                  )
                } else {
                  handleBulkCalculator(
                    start_price.value,
                    end_price.value,
                    bulk_amount.value,
                    perc_included.value,
                    safeFreeText,
                    appliesToEach.value,
                    upto.value,
                    extra.value
                  )
                }
              } else {
                handleUpdateValue({ freeTextValue: safeFreeText });
              }
            }}
            data={SAVE_OFFER_PREDICTIVE_TEXT}
          /></div>
        <div className="ml-1" style={{ width: "50%" }}>
          <DropDown2
            label="w/Coupon"
            data={ALL_COUPON_TYPES_PREDICTIVE_TEXT}
            width={80}
            value={withCoupon.value}
            onChange={(e) => {
              withCoupon.setValue(e);
              if (bulkCalculator) {
                // In calculator mode: update coupon in text without recalculation
                // Get current offer text and update just the coupon part
                const sortedCouponTypes = [...ALL_COUPON_TYPES_PREDICTIVE_TEXT]
                  .filter((c) => c.id)
                  .sort((a, b) => b.id.length - a.id.length);
                
                let updatedText = currentOffer?.offer_text || value || '';
                
                // Remove old coupon (w/ prefix or just text at end)
                for (const coupon of sortedCouponTypes) {
                  const couponPattern = new RegExp(`\\s*(w/)?${coupon.id}\\s*$`);
                  if (couponPattern.test(updatedText)) {
                    updatedText = updatedText.replace(couponPattern, '').trim();
                    break;
                  }
                }
                
                // Add new coupon (calculator format)
                if (e) {
                  updatedText = `${updatedText} w/${e}`;
                }
                onChange(updatedText);
              } else {
                handleUpdateValue({ withCouponValue: e });
              }
            }}
          /></div>
      </Container>
    </BuilderContainer>
  );
};

const BuilderContainer = styled.div`
  padding:8px 0;
  .text {
    font-size:12px;
  }
`;

const Title = styled.div`
  color: #66c0e1;
  font-size: 16px;
`;
const Container = styled.div`
  background: ${(props) => props.theme.palette.items};
  padding: 8px;
  display: flex;
  margin:2px 0;
`;
